import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  BarChart3,
  Check,
  ClipboardCheck,
  Download,
  FileSpreadsheet,
  HardDrive,
  Rocket,
  Sparkles,
  User,
  UserCog,
  Users,
  WalletCards,
  Video,
} from 'lucide-react';
import { PublicLayout } from '../../components/layout';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  createXpaySubscriptionCheckout,
  redirectToXpay,
} from '../../services/xpayService';
import { getPublicPricingCatalog } from '../../services/pricingCatalogService';
import {
  BillingCycle,
  PLAN_ENTITLEMENTS,
  PublicPricingCatalog,
  PublicPricingPlan,
  SubscriptionPlanId,
} from '../../types/subscription';
import { formatNumber } from '../../utils/localization';

const PLAN_ICONS: Record<SubscriptionPlanId, typeof Sparkles> = {
  trial: Sparkles,
  solo: User,
  pro: Rocket,
  center: Users,
  enterprise: Users,
};

const Pricing: React.FC = () => {
  const { t } = useTranslation('pricing');
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const [catalog, setCatalog] = useState<PublicPricingCatalog | null>(null);
  const [catalogError, setCatalogError] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<SubscriptionPlanId | null>(null);

  const faq = t('faq', { returnObjects: true }) as Array<{ q: string; a: string }>;
  const formatPrice = (value: number) =>
    language === 'ar'
      ? `${formatNumber(value, language)} ج.م`
      : `EGP ${formatNumber(value, language)}`;
  const formatGb = (value: number) =>
    language === 'ar'
      ? `${formatNumber(value, language)} جيجابايت`
      : `${formatNumber(value, language)} GB`;
  const annualSavings = (plan: PublicPricingPlan) => {
    if (!plan.yearlyPriceEgp || plan.monthlyPriceEgp <= 0) return null;
    const monthlyAnnualEquivalentEgp = plan.monthlyPriceEgp * 12;
    const annualSavingsEgp = Math.max(
      0,
      monthlyAnnualEquivalentEgp - plan.yearlyPriceEgp
    );
    return {
      annualSavingsEgp,
      annualSavingsMonths: annualSavingsEgp / plan.monthlyPriceEgp,
    };
  };

  useEffect(() => {
    getPublicPricingCatalog()
      .then(setCatalog)
      .catch(() => setCatalogError(true));
  }, []);

  const handleSubscribe = async (plan: PublicPricingPlan) => {
    if (plan.id === 'trial') {
      navigate('/register');
      return;
    }
    if (plan.isContactOnly || plan.id === 'enterprise') {
      navigate('/contact');
      return;
    }
    if (!user) {
      navigate('/login', { state: { returnTo: '/pricing' } });
      return;
    }

    setLoadingPlan(plan.id);
    try {
      const checkout = await createXpaySubscriptionCheckout(plan.id, billing);
      await redirectToXpay(checkout.redirectUrl);
    } catch (error: any) {
      const code = error?.code;
      if (code === 'functions/unauthenticated') {
        toast.error(t('loginRequired'));
        navigate('/login', { state: { returnTo: '/pricing' } });
      } else {
        toast.error(t('paymentStartError'));
      }
    } finally {
      setLoadingPlan(null);
    }
  };

  const planLimitRows = (plan: PublicPricingPlan) => [
    {
      icon: Users,
      label: t('limits.students'),
      value: t('limits.studentsValue', {
        included: formatNumber(plan.students?.included ?? plan.includedActiveStudents, language),
        maximum:
          plan.students?.recommendedMaximum === undefined
            ? t('contractBased')
            : formatNumber(plan.students.recommendedMaximum, language),
      }),
    },
    {
      icon: WalletCards,
      label: t('limits.studentOverage'),
      value:
        plan.students?.blockPriceEgp && plan.students.blockPriceEgp > 0
          ? t('limits.studentOverageValue', {
              price: formatPrice(plan.students.blockPriceEgp),
              block: formatNumber(plan.students.blockSize, language),
            })
          : t('notAvailable'),
    },
    {
      icon: HardDrive,
      label: t('limits.storage'),
      value: t('limits.storageValue', {
        included: formatGb(plan.storage?.includedGb ?? 0),
        price: formatPrice(plan.storage?.blockPriceEgp ?? 0),
        block: formatGb(plan.storage?.blockSizeGb ?? 0),
      }),
    },
    {
      icon: Download,
      label: t('limits.bandwidth'),
      value: plan.bandwidth?.reliableMetering
        ? t('limits.bandwidthValue', {
            included: formatGb(plan.bandwidth.includedGb),
            price: formatPrice(plan.bandwidth.blockPriceEgp),
            block: formatGb(plan.bandwidth.blockSizeGb),
          })
        : t('limits.bandwidthNotBilled', {
            included: formatGb(plan.bandwidth?.includedGb ?? 0),
          }),
    },
    {
      icon: UserCog,
      label: t('limits.employees'),
      value:
        plan.limits?.employees && plan.limits.employees > 0
          ? t('limits.employeesValue', {
              employeeCount: formatNumber(plan.limits.employees, language),
            })
          : t('limits.noEmployees'),
    },
  ];

  const planFeatureRows = (plan: PublicPricingPlan) => [
    plan.features.assignments ? t('features.assignments') : null,
    plan.features.parents ? t('features.parents') : null,
    plan.features.zoomIntegration ? t('features.zoom') : null,
    plan.features.paymentIntegration ? t('features.payments') : null,
    plan.features.accounting === 'advanced'
      ? t('features.advancedAccounting')
      : t('features.basicAccounting'),
    plan.features.reports === 'advanced'
      ? t('features.advancedReports')
      : t('features.basicReports'),
    plan.features.excelExport ? t('features.excelExport') : null,
    plan.features.employeeManagement ? t('features.employeeManagement') : null,
    plan.features.permissionManagement ? t('features.permissions') : null,
    plan.features.advancedAssignmentReports
      ? t('features.advancedAssignmentReports')
      : null,
    plan.features.assignmentExcelExport ? t('features.assignmentExcelExport') : null,
    plan.features.assignmentsManagedByEmployees
      ? t('features.staffAssignments')
      : null,
    plan.features.assignmentSubmissionAnalytics
      ? t('features.submissionAnalytics')
      : null,
    plan.features.lateSubmissionAnalytics ? t('features.lateAnalytics') : null,
    plan.features.assignmentPerformanceComparison
      ? t('features.performanceComparison')
      : null,
  ].filter(Boolean) as string[];

  return (
    <PublicLayout>
      <div className='min-h-screen bg-gray-50 dark:bg-secondary-950'>
        <section className='bg-gradient-to-br from-primary-700 via-primary-600 to-indigo-700 px-4 py-12 text-white md:py-14'>
          <div className='mx-auto flex max-w-6xl flex-col items-center gap-6 text-center'>
            <div className='space-y-4'>
              <span className='inline-block rounded-full bg-white/20 px-4 py-1.5 text-sm font-semibold'>
                {t('badge')}
              </span>
              <h1 className='text-3xl font-extrabold leading-tight md:text-5xl'>
                {t('title')}
              </h1>
              <p className='mx-auto max-w-2xl text-base leading-7 text-primary-100 md:text-lg'>
                {t('subtitle')}
              </p>
            </div>
            <div className='inline-flex gap-1 rounded-xl bg-white/15 p-1'>
              {(['monthly', 'yearly'] as BillingCycle[]).map(cycle => (
                <button
                  key={cycle}
                  onClick={() => setBilling(cycle)}
                  className={`rounded-lg px-5 py-2 text-sm font-semibold transition-all ${
                    billing === cycle
                      ? 'bg-white text-primary-700 shadow'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  {cycle === 'monthly' ? t('monthly') : t('yearly')}
                </button>
              ))}
            </div>
            {billing === 'yearly' ? (
              <p className='max-w-2xl text-sm leading-6 text-primary-100'>
                {t('annualUsageNote')}
              </p>
            ) : null}
          </div>
        </section>

        <main className='mx-auto max-w-7xl px-4 py-12'>
          {catalogError ? (
            <div className='rounded-2xl border border-red-200 bg-red-50 p-8 text-center text-red-800'>
              {t('catalogError')}
            </div>
          ) : !catalog ? (
            <div className='flex min-h-[20rem] items-center justify-center'>
              <div className='h-9 w-9 animate-spin rounded-full border-4 border-primary-500 border-t-transparent' />
            </div>
          ) : (
            <>
              <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
                {catalog.plans.map(plan => {
                  const entitlement = PLAN_ENTITLEMENTS[plan.id];
                  const Icon = PLAN_ICONS[plan.id];
                  const savings = annualSavings(plan);
                  const isYearly = billing === 'yearly' && plan.yearlyPriceEgp !== null;
                  const price = isYearly ? plan.yearlyPriceEgp! : plan.monthlyPriceEgp;
                  return (
                    <article
                      key={plan.id}
                      className={`relative flex min-h-full flex-col rounded-2xl border-2 bg-white p-6 shadow-sm transition-shadow hover:shadow-lg dark:bg-secondary-900 ${
                        plan.isPopular
                          ? 'border-primary-500 ring-2 ring-primary-100'
                          : 'border-gray-200 dark:border-secondary-800'
                      }`}
                    >
                      {plan.isPopular ? (
                        <span className='absolute -top-4 right-1/2 translate-x-1/2 rounded-full bg-primary-600 px-4 py-1.5 text-xs font-bold text-white'>
                          {t('popular')}
                        </span>
                      ) : null}
                      <div className='flex items-start justify-between gap-4'>
                        <div className='min-w-0'>
                          <h2 className='text-xl font-bold text-gray-900 dark:text-white'>
                            {plan.nameAr}
                          </h2>
                          <p className='mt-2 min-h-[3rem] text-sm leading-6 text-gray-500 dark:text-gray-300'>
                            {plan.audienceAr}
                          </p>
                        </div>
                        <div
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                            plan.isPopular
                              ? 'bg-primary-50 text-primary-600'
                              : 'bg-gray-100 text-gray-700 dark:bg-secondary-800 dark:text-secondary-200'
                          }`}
                        >
                          <Icon className='h-5 w-5' aria-hidden='true' />
                        </div>
                      </div>

                      <div className='my-6 border-y border-gray-100 py-5 dark:border-secondary-800'>
                        {plan.id === 'trial' ? (
                          <span className='text-3xl font-extrabold dark:text-white'>
                            {t('free')}
                          </span>
                        ) : plan.isContactOnly ? (
                          <>
                            <span className='text-sm text-gray-500'>{t('startsFrom')}</span>
                            <div className='text-3xl font-extrabold dark:text-white'>
                              {formatPrice(plan.monthlyPriceEgp)}
                            </div>
                          </>
                        ) : (
                          <div className='flex flex-wrap items-end gap-2'>
                            <span className='text-3xl font-extrabold dark:text-white'>
                              {formatPrice(price)}
                            </span>
                            <span className='pb-1 text-sm text-gray-500 dark:text-gray-400'>
                              / {isYearly ? t('perYear') : t('perMonth')}
                            </span>
                          </div>
                        )}
                        {isYearly && savings && savings.annualSavingsEgp > 0 ? (
                          <div className='mt-3 flex flex-wrap gap-2 text-sm'>
                            <span className='rounded-full bg-green-50 px-3 py-1 font-semibold text-green-700'>
                              {t('saveAmount', {
                                amount: formatPrice(savings.annualSavingsEgp),
                              })}
                            </span>
                            {Math.round(savings.annualSavingsMonths) === catalog.annualSavingsMonths ? (
                              <span className='rounded-full bg-primary-50 px-3 py-1 font-semibold text-primary-700'>
                                {t('twoMonthsFree')}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className='rounded-xl bg-gray-50 p-4 text-sm dark:bg-secondary-800 dark:text-secondary-100'>
                        <h3 className='mb-3 font-bold text-gray-900 dark:text-white'>
                          {t('limits.title')}
                        </h3>
                        <div className='space-y-3'>
                          {planLimitRows(plan).map(row => {
                            const RowIcon = row.icon;
                            return (
                              <div key={row.label} className='flex items-start gap-3'>
                                <RowIcon className='mt-0.5 h-4 w-4 shrink-0 text-primary-600' />
                                <div className='min-w-0'>
                                  <p className='font-semibold text-gray-900 dark:text-white'>
                                    {row.label}
                                  </p>
                                  <p className='mt-0.5 leading-6 text-gray-500 dark:text-gray-300'>
                                    {row.value}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className='mt-5 flex-1'>
                        <h3 className='font-bold text-gray-900 dark:text-white'>
                          {t('features.title')}
                        </h3>
                        <ul className='mt-3 grid gap-2'>
                          {planFeatureRows(plan).map(feature => (
                            <li
                              key={feature}
                              className='flex items-start gap-2 text-sm leading-6 text-gray-600 dark:text-gray-300'
                            >
                              <Check className='mt-1 h-4 w-4 shrink-0 text-green-500' />
                              <span>{feature}</span>
                            </li>
                          ))}
                          {entitlement.featuresAr
                            .filter(feature => !planFeatureRows(plan).includes(feature))
                            .map(feature => (
                              <li
                                key={feature}
                                className='flex items-start gap-2 text-sm leading-6 text-gray-600 dark:text-gray-300'
                              >
                                <Check className='mt-1 h-4 w-4 shrink-0 text-green-500' />
                                <span>{feature}</span>
                              </li>
                            ))}
                        </ul>
                      </div>

                      <button
                        onClick={() => handleSubscribe(plan)}
                        disabled={loadingPlan === plan.id}
                        className={`mt-6 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-colors disabled:opacity-60 ${
                          plan.isPopular
                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                            : 'bg-gray-900 text-white hover:bg-gray-800'
                        }`}
                      >
                        {plan.features.excelExport ? (
                          <FileSpreadsheet className='h-4 w-4' aria-hidden='true' />
                        ) : plan.features.advancedAssignmentReports ? (
                          <BarChart3 className='h-4 w-4' aria-hidden='true' />
                        ) : plan.features.assignments ? (
                          <ClipboardCheck className='h-4 w-4' aria-hidden='true' />
                        ) : (
                          <Video className='h-4 w-4' aria-hidden='true' />
                        )}
                        {loadingPlan === plan.id
                          ? t('loading')
                          : plan.id === 'trial'
                          ? t('startTrial')
                          : plan.isContactOnly
                          ? t('requestQuote')
                          : t('subscribeNow')}
                      </button>
                    </article>
                  );
                })}
              </div>

              <section className='mt-14 rounded-2xl border border-amber-200 bg-amber-50 p-6 dark:border-amber-800 dark:bg-amber-950/30'>
                <h2 className='font-bold text-amber-950 dark:text-amber-100'>{t('thirdParty')}</h2>
                <ul className='mt-3 space-y-2 text-sm text-amber-900'>
                  {catalog.thirdPartyCostsAr.map(item => (
                    <li key={item}>• {item}</li>
                  ))}
                </ul>
              </section>
            </>
          )}

          <section className='mx-auto mt-20 max-w-3xl'>
            <h2 className='mb-8 text-center text-2xl font-bold text-gray-900'>
              {t('faqTitle')}
            </h2>
            <div className='space-y-3'>
              {faq.map((item, index) => (
                <div key={item.q} className='overflow-hidden rounded-xl border bg-white dark:border-secondary-800 dark:bg-secondary-900'>
                  <button
                    className='flex w-full items-center justify-between p-5 text-start font-semibold dark:text-white'
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span>{item.q}</span>
                    <span>{openFaq === index ? '−' : '+'}</span>
                  </button>
                  {openFaq === index ? (
                    <p className='border-t px-5 py-4 text-sm leading-7 text-gray-600 dark:border-secondary-800 dark:text-gray-300'>
                      {item.a}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>
    </PublicLayout>
  );
};

export default Pricing;
